import { useEffect, useState } from "react";
import { toast } from "sonner";
import { listBranches, createBranch, updateBranch, deleteBranch } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Building2, Pencil, Plus, Trash2, CheckCircle2 } from "lucide-react";

const emptyForm = { name: "", active: true };

export default function Branches() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const { branchId, setBranchId, refreshBranches, activeBranch } = useBranch();

  const load = async () => {
    setLoading(true);
    try {
      setRows(await listBranches());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (b) => {
    setForm({ name: b.name, active: b.active !== false });
    setEditingId(b.id);
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Branch name required");
      return;
    }
    try {
      if (editingId) {
        await updateBranch(editingId, { name: form.name.trim(), active: !!form.active });
        toast.success("Branch updated");
      } else {
        await createBranch({ name: form.name.trim(), active: !!form.active });
        toast.success("Branch added");
      }
      setDialogOpen(false);
      await load();
      await refreshBranches();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteBranch(deleteId);
      toast.success("Branch deleted. Its data was moved to Punalur.");
      setDeleteId(null);
      await load();
      await refreshBranches();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="branches-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Setup</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1">Branches</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Each branch keeps its own delivery boys and collection entries. Same formulas apply everywhere.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNew}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg"
              data-testid="add-branch-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add Branch
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-md">
            <form onSubmit={submit}>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  {editingId ? "Edit Branch" : "Add Branch"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label htmlFor="branch-name">Branch Name</Label>
                  <Input
                    id="branch-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Kollam"
                    data-testid="branch-name-input"
                    required
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-zinc-200 p-3">
                  <div>
                    <div className="text-sm font-medium">Active</div>
                    <div className="text-xs text-zinc-500">Inactive branches remain but can be hidden later.</div>
                  </div>
                  <Switch
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                    data-testid="branch-active-switch"
                  />
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  data-testid="branch-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="branch-save-btn"
                >
                  {editingId ? "Save Changes" : "Add"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <Th>Branch</Th>
              <Th>Status</Th>
              <Th>Active on this device</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4} className="text-center py-8 text-zinc-500">Loading…</td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-10 text-zinc-500">No branches yet.</td>
              </tr>
            )}
            {rows.map((b) => (
              <tr
                key={b.id}
                className="border-b border-zinc-100 hover:bg-zinc-50"
                data-testid={`branch-row-${b.name}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 font-medium text-zinc-950">
                    <Building2 className="h-4 w-4 text-emerald-700" />
                    {b.name}
                    {b.name === "Punalur" && (
                      <Badge variant="outline" className="ml-1 border-emerald-200 text-emerald-700 bg-emerald-50">
                        Default
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {b.active !== false ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">Active</Badge>
                  ) : (
                    <Badge variant="outline" className="border-zinc-200 text-zinc-500">Inactive</Badge>
                  )}
                </td>
                <td className="px-4 py-3">
                  {branchId === b.id ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Currently viewing
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setBranchId(b.id)}
                      data-testid={`branch-switch-${b.name}`}
                    >
                      Switch to this branch
                    </Button>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(b)}
                    data-testid={`branch-edit-${b.name}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeleteId(b.id)}
                    className="text-rose-600 hover:text-rose-700"
                    disabled={b.name === "Punalur" || rows.length <= 1}
                    data-testid={`branch-delete-${b.name}`}
                    title={b.name === "Punalur" ? "Default branch cannot be deleted" : "Delete"}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-zinc-500">
        Currently viewing: <span className="font-semibold text-zinc-800">{activeBranch?.name || "—"}</span>. All dashboards, delivery boys, collections and reports are filtered to this branch.
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this branch?</AlertDialogTitle>
            <AlertDialogDescription>
              Any delivery boys and collection entries under this branch will be moved to <b>Punalur</b>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={confirmDelete}
              data-testid="branch-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 ${className}`}>
    {children}
  </th>
);
